import { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import MainLayout from '../components/MainLayout'
import NoteEditor from '../components/NoteEditor'
import ArticleView from '../components/ArticleView'
import TagSelector from '../components/TagSelector'
import { Note, Tag, getNote, updateSourceUrl } from '../api/notes'
import apiClient from '../api/client'

export default function NoteDetailPage() {
  const { noteId } = useParams<{ noteId: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  const [note, setNote] = useState<Note | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [hasChanges, setHasChanges] = useState(false)
  const [editedContent, setEditedContent] = useState('')
  const [editedName, setEditedName] = useState('')
  const [editedTags, setEditedTags] = useState<Tag[]>([])
  const [isEditingName, setIsEditingName] = useState(false)

  // Source URL state
  const [editedSourceUrl, setEditedSourceUrl] = useState<string>('')
  const [isEditingSourceUrl, setIsEditingSourceUrl] = useState(false)
  const [isSavingSourceUrl, setIsSavingSourceUrl] = useState(false)
  const sourceUrlInputRef = useRef<HTMLInputElement>(null)

  // Track last saved content to compare for changes
  const lastSavedContentRef = useRef('')
  const lastSavedNameRef = useRef('')

  // Track current edited values for comparison (refs don't cause re-renders)
  const currentContentRef = useRef('')
  const currentNameRef = useRef('')

  // Determine view mode from note or URL
  const viewMode = note?.view_mode || searchParams.get('mode') || 'view'
  const isEditMode = viewMode === 'edit'
  const isPdf = note?.file_type === 'pdf'

  // Fetch note on mount
  useEffect(() => {
    if (noteId && noteId !== 'new') {
      fetchNote()
    } else {
      // New note
      setNote({
        id: '',
        name: '',
        content: '',
        full_content: '',
        file_name: null,
        file_type: 'html',
        source_url: null,
        is_archived: false,
        is_starred: false,
        is_pinned: false,
        is_home: false,
        is_thrashed: false,
        view_mode: 'edit',
        tags: [],
        created_time: Date.now() / 1000,
        modified_time: Date.now() / 1000,
        order: null,
      })
      setEditedContent('')
      setEditedName('Untitled Note')
      setEditedTags([])
      setEditedSourceUrl('')
      // Set initial refs for new note
      lastSavedContentRef.current = ''
      lastSavedNameRef.current = 'Untitled Note'
      currentContentRef.current = ''
      currentNameRef.current = 'Untitled Note'
      setHasChanges(false)
      setIsLoading(false)
    }
  }, [noteId])

  const fetchNote = async () => {
    if (!noteId) return

    setIsLoading(true)
    setError(null)
    try {
      const data = await getNote(noteId)
      setNote(data)
      const content = data.full_content || data.content || ''
      const name = data.name || 'Untitled'
      setEditedContent(content)
      setEditedName(name)
      setEditedTags(data.tags || [])
      setEditedSourceUrl(data.source_url || '')
      // Store as last saved and current values
      lastSavedContentRef.current = content
      lastSavedNameRef.current = name
      currentContentRef.current = content
      currentNameRef.current = name
      setHasChanges(false)
    } catch (err) {
      console.error('Failed to fetch note:', err)
      setError('Failed to load note. It may have been deleted.')
    } finally {
      setIsLoading(false)
    }
  }

  // Helper to check if there are unsaved changes
  const updateHasChanges = useCallback(() => {
    const contentChanged = currentContentRef.current !== lastSavedContentRef.current
    const nameChanged = currentNameRef.current !== lastSavedNameRef.current
    setHasChanges(contentChanged || nameChanged)
  }, [])

  // Handle content change
  const handleContentChange = useCallback((newContent: string) => {
    setEditedContent(newContent)
    currentContentRef.current = newContent
    updateHasChanges()
  }, [updateHasChanges])

  // Handle save
  const handleSave = useCallback(async () => {
    if (!note) return
    if (isSaving) return

    // Use refs to get current values (avoids stale closure issues)
    const contentToSave = currentContentRef.current
    const nameToSave = currentNameRef.current

    setIsSaving(true)
    try {
      if (note.id) {
        // Update existing note
        await apiClient.put(`/notes/update/${note.id}`, {
          id: note.id,
          name: nameToSave,
          full_content: contentToSave,
          view_mode: note.view_mode,
        })
        setNote({ ...note, name: nameToSave, full_content: contentToSave })
        setEditedName(nameToSave)
        setEditedContent(contentToSave)
      } else {
        // Create new note
        const response = await apiClient.post('/notes/create', {
          name: nameToSave,
          full_content: contentToSave,
          view_mode: 'edit',
          tags: editedTags.map(t => ({ id: t.id, name: t.name })),
        })
        const newNote = response.data
        setNote(newNote)
        setEditedTags(newNote.tags || [])
        // Update URL without adding to history
        window.history.replaceState(null, '', `/notes/${newNote.id}`)
      }
      // Update last saved refs with the actual values we just saved
      lastSavedContentRef.current = contentToSave
      lastSavedNameRef.current = nameToSave
      setHasChanges(false)
    } catch (err) {
      console.error('Failed to save note:', err)
      setError('Failed to save note. Please try again.')
    } finally {
      setIsSaving(false)
    }
  }, [note, isSaving, editedTags])

  // Handle name change
  const handleNameChange = (newName: string) => {
    setEditedName(newName)
    currentNameRef.current = newName
    updateHasChanges()
  }

  // Handle name save
  const handleNameSave = async () => {
    setIsEditingName(false)
    if (note?.id && editedName !== note.name) {
      await handleSave()
    }
  }

  // Handle tag changes
  const handleTagsChange = async (newTags: Tag[]) => {
    setEditedTags(newTags)

    // If note exists, update tags immediately via API
    if (note?.id) {
      try {
        await apiClient.put('/notes/update_note_tags', {
          note_ids: [note.id],
          tags: newTags.map(t => ({ id: t.id, name: t.name })),
        })
        setNote({ ...note, tags: newTags })
      } catch (err) {
        console.error('Failed to update tags:', err)
        setError('Failed to update tags')
        // Revert to previous tags on error
        setEditedTags(note.tags || [])
      }
    }
  }

  // Handle back navigation
  const handleBack = async () => {
    // Check for actual unsaved changes by comparing refs
    const contentChanged = currentContentRef.current !== lastSavedContentRef.current
    const nameChanged = currentNameRef.current !== lastSavedNameRef.current
    const actuallyHasChanges = contentChanged || nameChanged

    if (actuallyHasChanges) {
      const confirmed = window.confirm('You have unsaved changes. Are you sure you want to leave?')
      if (!confirmed) return
    }
    navigate(-1)
  }

  // Toggle view mode
  const toggleViewMode = async () => {
    if (!note) return

    const newMode = isEditMode ? 'view' : 'edit'

    // Save current content before switching
    if (hasChanges && isEditMode) {
      await handleSave()
    }

    // Update note view mode
    if (note.id) {
      try {
        await apiClient.put('/notes/partial_update_note', {
          note_ids: [note.id],
          note_partial: { view_mode: newMode },
        })
      } catch (err) {
        console.error('Failed to update view mode:', err)
      }
    }

    setNote({ ...note, view_mode: newMode })
  }

  // Toggle star
  const toggleStar = async () => {
    if (!note?.id) return

    try {
      await apiClient.put('/notes/partial_update_note', {
        note_ids: [note.id],
        note_partial: { is_starred: !note.is_starred },
      })
      setNote({ ...note, is_starred: !note.is_starred })
    } catch (err) {
      console.error('Failed to toggle star:', err)
    }
  }

  // Toggle home
  const toggleHome = async () => {
    if (!note?.id) return

    try {
      await apiClient.put('/notes/partial_update_note', {
        note_ids: [note.id],
        note_partial: { is_home: !note.is_home },
      })
      setNote({ ...note, is_home: !note.is_home })
    } catch (err) {
      console.error('Failed to toggle home:', err)
    }
  }

  // Toggle archive
  const toggleArchive = async () => {
    if (!note?.id) return

    try {
      await apiClient.put('/notes/partial_update_note', {
        note_ids: [note.id],
        note_partial: { is_archived: !note.is_archived },
      })
      setNote({ ...note, is_archived: !note.is_archived })
    } catch (err) {
      console.error('Failed to toggle archive:', err)
    }
  }

  // Delete note (move to trash)
  const handleDelete = async () => {
    if (!note?.id) return

    const confirmed = window.confirm(note.is_thrashed
      ? 'This will permanently delete the note. Are you sure?'
      : 'Move this note to trash?'
    )
    if (!confirmed) return

    try {
      if (note.is_thrashed) {
        // Permanent delete
        await apiClient.delete(`/notes/${note.id}`)
      } else {
        // Move to trash
        await apiClient.put('/notes/partial_update_note', {
          note_ids: [note.id],
          note_partial: { is_thrashed: true },
        })
      }
      navigate('/notes')
    } catch (err) {
      console.error('Failed to delete note:', err)
    }
  }

  // Restore from trash
  const handleRestore = async () => {
    if (!note?.id) return

    try {
      await apiClient.put('/notes/partial_update_note', {
        note_ids: [note.id],
        note_partial: { is_thrashed: false },
      })
      setNote({ ...note, is_thrashed: false })
    } catch (err) {
      console.error('Failed to restore note:', err)
    }
  }

  // Ensure a URL is always treated as external (prepend https:// if no protocol present)
  const ensureAbsoluteUrl = (url: string): string => {
    if (!url) return url
    return /^[a-zA-Z][a-zA-Z\d+\-.]*:\/\//.test(url) ? url : `https://${url}`
  }

  // Source URL handlers
  const handleOpenSourceUrlEdit = () => {
    setEditedSourceUrl(note?.source_url || '')
    setIsEditingSourceUrl(true)
    // Focus the input on next render tick
    setTimeout(() => sourceUrlInputRef.current?.focus(), 0)
  }

  const handleSaveSourceUrl = async () => {
    if (!note?.id) return
    const trimmed = editedSourceUrl.trim()
    // Nothing changed — just close
    if (trimmed === (note.source_url || '')) {
      setIsEditingSourceUrl(false)
      return
    }
    setIsSavingSourceUrl(true)
    try {
      await updateSourceUrl(note.id, trimmed || null)
      setNote({ ...note, source_url: trimmed || null })
      setIsEditingSourceUrl(false)
    } catch (err) {
      console.error('Failed to update source URL:', err)
      setError('Failed to update source URL')
    } finally {
      setIsSavingSourceUrl(false)
    }
  }

  const handleCancelSourceUrlEdit = () => {
    setEditedSourceUrl(note?.source_url || '')
    setIsEditingSourceUrl(false)
  }

  const handleSourceUrlKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleSaveSourceUrl()
    if (e.key === 'Escape') handleCancelSourceUrlEdit()
  }

  // Loading state
  if (isLoading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
        </div>
      </MainLayout>
    )
  }

  // Error state
  if (error && !note) {
    return (
      <MainLayout>
        <div className="flex flex-col items-center justify-center h-64">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Note not found</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => navigate('/notes')}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-secondary transition-colors"
          >
            Back to Notes
          </button>
        </div>
      </MainLayout>
    )
  }

  return (
    <MainLayout>
      {/* Note Header */}
      <div className="mb-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        {/* Back button and title */}
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <button
            onClick={handleBack}
            data-testid="note-back-btn"
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg flex-shrink-0 touch-manipulation"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>

          {/* Editable title */}
          {isEditingName ? (
            <input
              type="text"
              value={editedName}
              onChange={(e) => handleNameChange(e.target.value)}
              onBlur={handleNameSave}
              onKeyDown={(e) => e.key === 'Enter' && handleNameSave()}
              data-testid="note-title-input"
              className="flex-1 text-xl sm:text-2xl font-bold text-gray-900 bg-transparent border-b-2 border-primary focus:outline-none min-w-0"
              autoFocus
            />
          ) : (
            <div className="flex items-center gap-2 min-w-0 flex-1">
            <h1
              data-testid="note-title"
              className="text-xl sm:text-2xl font-bold text-gray-900 truncate cursor-pointer hover:text-primary transition-colors"
              onClick={() => !isPdf && setIsEditingName(true)}
              title="Click to edit"
            >
              {editedName || 'Untitled'}
            </h1>
            {isPdf && (
              <span
                data-testid="pdf-badge"
                className="flex-shrink-0 px-2 py-0.5 text-xs font-semibold text-red-600 bg-red-100 rounded-full"
              >
                PDF
              </span>
            )}
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
          {/* Save button — hidden for PDF notes */}
          {!isPdf && (
          <button
            onClick={handleSave}
            disabled={isSaving}
            data-testid="note-save-btn"
            className={`p-2 sm:px-3 sm:py-2 rounded-lg transition-colors flex items-center gap-1 touch-manipulation ${
              hasChanges
                ? 'bg-primary text-white hover:bg-secondary cursor-pointer'
                : 'bg-gray-100 text-gray-400 cursor-default opacity-50'
            } ${isSaving ? 'opacity-70 cursor-wait' : ''}`}
            title="Save (Ctrl+S)"
          >
            {isSaving ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
              </svg>
            )}
            <span className="hidden sm:inline text-sm font-medium">Save</span>
          </button>
          )}

          {/* View/Edit toggle - not for PDFs */}
          {!isPdf && (
            <button
              onClick={toggleViewMode}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg touch-manipulation"
              title={isEditMode ? 'Switch to View mode' : 'Switch to Edit mode'}
            >
              {isEditMode ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              )}
            </button>
          )}

          {/* Star button */}
          <button
            onClick={toggleStar}
            disabled={!note?.id}
            data-testid="note-star-btn"
            className={`p-2 rounded-lg transition-colors touch-manipulation ${
              note?.is_starred ? 'text-yellow-500' : 'text-gray-400 hover:text-gray-600'
            } ${!note?.id ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-100'}`}
            title={note?.is_starred ? 'Remove from starred' : 'Add to starred'}
          >
            <svg className="w-5 h-5" fill={note?.is_starred ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
            </svg>
          </button>

          {/* Home button */}
          <button
            onClick={toggleHome}
            disabled={!note?.id}
            data-testid="note-home-btn"
            className={`p-2 rounded-lg transition-colors touch-manipulation ${
              note?.is_home ? 'text-primary' : 'text-gray-400 hover:text-gray-600'
            } ${!note?.id ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-100'}`}
            title={note?.is_home ? 'Remove from home' : 'Pin to home'}
          >
            <svg className="w-5 h-5" fill={note?.is_home ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
          </button>

          {/* Archive button */}
          <button
            onClick={toggleArchive}
            disabled={!note?.id}
            data-testid="note-archive-btn"
            className={`hidden sm:block p-2 rounded-lg transition-colors touch-manipulation ${
              note?.is_archived ? 'text-primary' : 'text-gray-400 hover:text-gray-600'
            } ${!note?.id ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-100'}`}
            title={note?.is_archived ? 'Unarchive' : 'Archive'}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {note?.is_archived ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4l3 3m0 0l3-3m-3 3V4" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
              )}
            </svg>
          </button>

          {/* Delete/Restore button */}
          <button
            onClick={note?.is_thrashed ? handleRestore : handleDelete}
            disabled={!note?.id}
            data-testid={note?.is_thrashed ? 'note-restore-btn' : 'note-trash-btn'}
            className={`p-2 rounded-lg transition-colors touch-manipulation ${
              note?.is_thrashed ? 'text-green-500 hover:text-green-600' : 'text-gray-400 hover:text-red-500'
            } ${!note?.id ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-100'}`}
            title={note?.is_thrashed ? 'Restore from trash' : 'Move to trash'}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {note?.is_thrashed ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              )}
            </svg>
          </button>
        </div>
      </div>

      {/* Tag Selector */}
      <div className="mb-4">
        <TagSelector
          selectedTags={editedTags}
          onChange={handleTagsChange}
          placeholder="Add tags..."
          disabled={note?.is_thrashed}
        />
      </div>

      {/* Source URL row */}
      {(note?.source_url || note?.id) && (
        <div className="mb-4 flex items-center gap-2 min-w-0">
          {/* Link icon label */}
          <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
          </svg>

          {isEditingSourceUrl ? (
            /* Edit mode */
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <input
                ref={sourceUrlInputRef}
                type="url"
                value={editedSourceUrl}
                onChange={(e) => setEditedSourceUrl(e.target.value)}
                onKeyDown={handleSourceUrlKeyDown}
                placeholder="https://example.com"
                data-testid="note-source-url-input"
                className="flex-1 min-w-0 text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
              {/* Save */}
              <button
                onClick={handleSaveSourceUrl}
                disabled={isSavingSourceUrl}
                data-testid="note-source-url-save"
                className="p-1.5 text-green-600 hover:text-green-700 hover:bg-green-50 rounded-lg transition-colors disabled:opacity-50 flex-shrink-0"
                title="Save URL"
              >
                {isSavingSourceUrl ? (
                  <div className="w-4 h-4 border-2 border-green-600 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </button>
              {/* Cancel */}
              <button
                onClick={handleCancelSourceUrlEdit}
                data-testid="note-source-url-cancel"
                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
                title="Cancel"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ) : note?.source_url ? (
            /* Display mode — URL exists */
            <div className="flex items-center gap-1 flex-1 min-w-0">
              <a
                href={ensureAbsoluteUrl(note.source_url)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="note-source-url-link"
                className="text-sm text-primary hover:text-secondary hover:underline truncate flex-1 min-w-0"
                title={note.source_url}
              >
                {note.source_url}
              </a>
              {/* Open in new tab */}
              <a
                href={ensureAbsoluteUrl(note.source_url)}
                target="_blank"
                rel="noopener noreferrer"
                className="p-1.5 text-gray-400 hover:text-primary hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
                title="Open source URL"
                data-testid="note-source-url-open"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
              {/* Edit */}
              {!note.is_thrashed && (
                <button
                  onClick={handleOpenSourceUrlEdit}
                  data-testid="note-source-url-edit"
                  className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
                  title="Edit source URL"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>
              )}
            </div>
          ) : (
            /* Display mode — no URL yet */
            !note?.is_thrashed && (
              <button
                onClick={handleOpenSourceUrlEdit}
                data-testid="note-source-url-add"
                className="text-sm text-gray-400 hover:text-primary transition-colors"
                title="Add source URL"
              >
                Add source URL…
              </button>
            )
          )}
        </div>
      )}

      {/* Error notification */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center">
          <svg className="w-5 h-5 text-red-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
          <span className="text-sm text-red-700 flex-1">{error}</span>
          <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* Content area */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {isPdf ? (
          // PDF Viewer — use the authenticated /api/v1/notes/file/{id} endpoint
          <div
            data-testid="pdf-viewer"
            className="w-full"
            style={{ height: 'calc(100vh - 220px)' }}
          >
            <embed
              src={`/api/v1/notes/file/${note?.id}`}
              type="application/pdf"
              width="100%"
              height="100%"
              className="border-0"
            />
          </div>
        ) : isEditMode ? (
          // Jodit Editor - Centered with minimal padding on desktop
          <div className="w-full sm:max-w-7xl sm:mx-auto sm:px-4 md:px-6 lg:px-8">
            <NoteEditor
              content={editedContent}
              onChange={handleContentChange}
              onSave={handleSave}
              placeholder="Start writing your note..."
            />
          </div>
        ) : (
          // View mode - Wider article-style layout on desktop
          <div className="w-full sm:max-w-6xl sm:mx-auto sm:px-6 md:px-8 lg:px-12">
            <ArticleView content={editedContent} />
          </div>
        )}
      </div>

      {/* Unsaved changes indicator */}
      {hasChanges && (
        <div className="fixed bottom-4 right-4 bg-yellow-100 border border-yellow-300 text-yellow-800 px-4 py-2 rounded-lg shadow-lg flex items-center gap-2 animate-fade-in">
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          <span className="text-sm font-medium">Unsaved changes</span>
        </div>
      )}
    </MainLayout>
  )
}
